import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Card, Flex, Text } from "@mantine/core";
import { IconArrowRight } from "@tabler/icons-react";
import { HOME_SECTOR_CARDS, getSectorHref, type SectorCard } from "@/pages/home/sectorCatalog";
import { defaultSectorVisual, getSectorIcon, sectorVisualBySlug } from "@/pages/home/sectorCardVisual.util";
import classes from "./DiscoverSectorMarquee.module.css";

const SECTOR_CARD_BORDER = "#E2E8F0";
const SECTOR_CARD_TEXT = "#04305c";

function SectorMarqueeCard({
  sector,
  onSelect,
}: {
  sector: SectorCard;
  onSelect: (sector: SectorCard) => void;
}) {
  const SectorIcon = getSectorIcon(sector.slug);
  const visual = sectorVisualBySlug[sector.slug] ?? defaultSectorVisual;
  const isComingSoon = Boolean(sector.comingSoon);

  return (
    <Card
      withBorder
      radius="md"
      shadow="xs"
      p="xs"
      className={classes.sectorCard}
      style={{
        cursor: isComingSoon ? "default" : "pointer",
        borderColor: SECTOR_CARD_BORDER,
        backgroundColor: "#FFFFFF",
        opacity: isComingSoon ? 0.85 : 1,
      }}
      onClick={isComingSoon ? undefined : () => onSelect(sector)}
    >
      <Flex align="center" gap={8} wrap="nowrap" h="100%">
        <Box
          style={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 32,
            height: 32,
            borderRadius: 8,
            backgroundColor: visual.iconBg,
          }}
        >
          <SectorIcon size={16} color={visual.iconColor} />
        </Box>
        <Text fz={13} fw={500} c={SECTOR_CARD_TEXT} lh={1.3} lineClamp={2} style={{ minWidth: 0 }}>
          {sector.title}
        </Text>
      </Flex>
    </Card>
  );
}

function ViewAllCard({ onClick }: { onClick: () => void }) {
  return (
    <Card
      withBorder
      radius="md"
      shadow="xs"
      p="xs"
      className={classes.sectorCard}
      style={{
        cursor: "pointer",
        borderColor: SECTOR_CARD_BORDER,
        backgroundColor: "#FFFFFF",
      }}
      onClick={onClick}
    >
      <Flex align="center" justify="center" gap={6} h="100%">
        <Text fz={13} fw={600} c={SECTOR_CARD_TEXT}>
          View All Industries
        </Text>
        <IconArrowRight size={16} color={SECTOR_CARD_TEXT} />
      </Flex>
    </Card>
  );
}

type DiscoverSectorMarqueeProps = {
  onSectorSelect?: (sector: SectorCard) => void;
};

export default function DiscoverSectorMarquee({ onSectorSelect }: DiscoverSectorMarqueeProps) {
  const navigate = useNavigate();

  const sectors = useMemo(() => HOME_SECTOR_CARDS.filter((s) => !s.comingSoon), []);

  const handleSelect = (sector: SectorCard) => {
    if (onSectorSelect) {
      onSectorSelect(sector);
      return;
    }
    navigate(getSectorHref(sector.slug));
  };

  const handleViewAll = () => {
    const first = sectors[0];
    if (!first) return;
    if (onSectorSelect) {
      onSectorSelect(first);
      return;
    }
    navigate(getSectorHref(first.slug));
  };

  const trackItems = useMemo(() => {
    const chunk = [
      ...sectors.map((sector) => ({ kind: "sector" as const, sector })),
      { kind: "viewAll" as const },
    ];
    return [...chunk, ...chunk];
  }, [sectors]);

  return (
    <Box className={classes.root}>
      <Box className={classes.mask}>
        <Box className={classes.track}>
          {trackItems.map((item, index) =>
            item.kind === "sector" ? (
              <SectorMarqueeCard
                key={`${item.sector.slug}-${index}`}
                sector={item.sector}
                onSelect={handleSelect}
              />
            ) : (
              <ViewAllCard key={`view-all-${index}`} onClick={handleViewAll} />
            ),
          )}
        </Box>
      </Box>
    </Box>
  );
}
